/**
 * The single source of truth for reaching support.
 *
 * ## Why this module exists
 *
 * The concierge number used to be hardcoded in 20+ places across 8 files,
 * in three different formats (`4158740888`, `+14158740888`,
 * `(415) 874-0888`), behind two mutually incompatible `sms:` URL builders
 * — one of which forgot the iOS separator quirk, so half the app's SMS
 * deep-links silently dropped their pre-filled body on iPhone. Changing
 * the number meant finding all 20. Now it means changing this file.
 *
 * ## Tommy first — always
 *
 * The more important rule this module encodes: **no customer-facing CTA
 * opens an SMS composer.** Every "contact us" path in the app routes to
 * Tommy (`/support`) carrying the message it would have texted.
 *
 * The reason is escalation cost. Every SMS deep-link was an unconditional
 * page to a live human — for a seat request on a trip four months out, for
 * a gift-card redemption, for "how does this work". Routed through Tommy
 * instead, most of those get answered in chat for free, and the rest go to
 * his manager, who decides whether they're genuinely time-sensitive (live
 * concierge) or can be batched into the team's 24h queue. See
 * server/tommy-consult.js.
 *
 * So `buildSmsUrl` is deliberately NOT for CTAs. It exists for exactly one
 * caller: the post-triage handoff in Support.jsx, which only fires after
 * Stephanie has ruled a situation urgent. If you are reaching for it
 * anywhere else, you are re-opening the hole this module closed — use
 * `buildTommyUrl` instead.
 */

export const SUPPORT_SMS_NUMBER = '+14158740888';
export const SUPPORT_SMS_DISPLAY = '(415) 874-0888';

// Customers write to the team through the message form on the Contact page
// (founder, 2026-10-23); no support email address is published anywhere a
// customer reads it (the Terms and Privacy Policy keep their legal contacts).
// Signed-in messages land in the customer's own chat and on the inquiries
// desk; a guest also gives an email address to be answered at.
export const CONTACT_FORM_PATH = '/contact#message';
export const CONTACT_PAGE_DISPLAY = 'defera.app/contact';

// Security reports: vulnerabilities, suspected account compromise, phishing
// that impersonates Defera. Published on /security and in
// /.well-known/security.txt (the address researchers look for first).
export const SECURITY_EMAIL = 'security@defera.app';

// The voice concierge line: Norma, on Bland AI, answering 24/7 (founder,
// 2026-10-24, after calling her: "We need to switch everything to Bland AI.
// Remove phone number of Cartesia and switch it to the Bland AI one").
// Unlike SMS, calling IS a sanctioned CTA: the phone desk is AI-staffed with
// the knowledge base and files team requests through the same queue, so it
// costs nothing to page. The Cartesia line was +1 (415) 319-8553.
// NOTE: changing the number means changing ONLY these two lines (plus the
// same number in DEFERA_KNOWLEDGE_BASE, server/tommy-concierge.js, and
// public/llms.txt — parity rule).
export const SUPPORT_PHONE_NUMBER = '+14156854840';
export const SUPPORT_PHONE_DISPLAY = '+1 (415) 685-4840';
export const SUPPORT_PHONE_TEL = `tel:${SUPPORT_PHONE_NUMBER}`;

/** Concierge text line hours. The queue (24h SLA) is a separate channel. */
export const SUPPORT_HOURS_LABEL = 'Mon–Fri, 9 AM–6 PM PT';

/**
 * Query params Tommy reads on mount (see Support.jsx).
 *   ask — a message to open the conversation with, auto-sent on arrival
 *   ref — the booking reference in scope, so he can pull the trip up
 */
export const TOMMY_ASK_PARAM = 'ask';
export const TOMMY_REF_PARAM = 'ref';

/**
 * Route to Tommy with an opening message.
 *
 * `message` should read as the CUSTOMER speaking, in first person — it is
 * auto-sent as their first turn, so "I'd like to add a bag to my trip"
 * lands correctly and "Customer wants to add a bag" does not. It's the
 * same voice the old SMS bodies used, for the same reason.
 *
 * Returns a plain path so callers can hand it to <Link>, navigate(), or
 * an <a href>.
 */
export function buildTommyUrl({ message = '', bookingReference = '' } = {}) {
  const params = new URLSearchParams();
  const trimmed = String(message || '').trim();
  if (trimmed) params.set(TOMMY_ASK_PARAM, trimmed);
  const ref = String(bookingReference || '').trim();
  if (ref) params.set(TOMMY_REF_PARAM, ref);
  const qs = params.toString();
  return qs ? `/support?${qs}` : '/support';
}

/**
 * Build an `sms:` URL opening the native messaging app with the concierge
 * number and an optional pre-filled body.
 *
 * **Do not call this from a CTA.** See the module doc — the only
 * legitimate caller is the handoff Support.jsx reveals after Stephanie
 * triages a situation as urgent.
 *
 * iOS quirk: iPhone wants `&body=` rather than `?body=` on an `sms:` URL
 * with no other query string. Getting this wrong doesn't error — it
 * silently drops the pre-filled message, which is why it went unnoticed in
 * the six call sites that had it wrong.
 */
export function buildSmsUrl(body) {
  const isIos = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);
  const sep = isIos ? '&' : '?';
  const trimmed = String(body || '').trim();
  if (!trimmed) return `sms:${SUPPORT_SMS_NUMBER}`;
  return `sms:${SUPPORT_SMS_NUMBER}${sep}body=${encodeURIComponent(trimmed)}`;
}

/**
 * Opening messages for the CTAs that used to open a text composer.
 *
 * First person, because they're auto-sent as the customer's own turn.
 * Specific, because Tommy answers a specific question well and a vague one
 * badly — and because Stephanie's triage keys off what's actually being
 * asked. "I need help" gets a worse outcome than "I want to add a checked
 * bag to my trip to Tokyo".
 */
export const TOMMY_INTENTS = {
  requestFlightChange: 'I need to change my flight.',
  cancelHelp: 'I need help cancelling my trip.',
  refundQuestion: 'I have a question about getting a refund on my trip.',
  purchaseSeats: "I'd like to choose my seats.",
  changeSeats: "I'd like to change my seat selection.",
  purchaseBaggage: "I'd like to add baggage to my trip.",
  cabinUpgrade: "I'd like to look into a cabin upgrade.",
  confirmationNumber: "I've paid my trip off in full and I'd like my airline confirmation number.",
  pastDueHelp: "I need help with a past-due payment on my trip.",
  paymentIssue: "I'm having an issue with a payment on my trip.",
  checkoutIssue: "I'm having trouble completing my checkout.",
  bookingBlocked: "I'm not able to book right now and I'd like to understand why.",
  milesRedemption: "I'd like to redeem my Defera Miles.",
  general: '',
};

/**
 * Compose an intent with the trip it's about, so Tommy opens with the
 * booking already named instead of asking "which trip?".
 */
export function tommyIntentForBooking(intent, bookingReference, route) {
  const parts = [intent];
  if (route) parts.push(`(${route})`);
  else if (bookingReference) parts.push(`(booking ${bookingReference})`);
  return parts.join(' ');
}
